import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { blogAPI } from '../utils/api';

const FALLBACK_POSTS = []; // vide tant qu'aucun article n'est publié

export const Blog = () => {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SEO
    document.title = "Blog Sax&DJ — Conseils mariage, musique et événements en Île-de-France";

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };

    setMeta('name', 'description', "Conseils, inspirations et tendances pour réussir votre mariage ou soirée d'entreprise en Île-de-France. Articles rédigés par Sax&DJ, saxophoniste et DJ professionnel.");
    setLink('canonical', 'https://sax-dj.fr/blog');
    setMeta('property', 'og:title', "Blog Sax&DJ — Conseils mariage et événements");
    setMeta('property', 'og:url',   'https://sax-dj.fr/blog');

    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  useEffect(() => {
    blogAPI.getAll()
      .then(res => setPosts(res.data || FALLBACK_POSTS))
      .catch(() => setPosts(FALLBACK_POSTS))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">

      {/* Hero */}
      <section className="bg-gradient-to-br from-ivory via-pearl to-light-gold/20 py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="h-12 w-12 text-gold mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6 leading-tight">
            Le Blog Sax&DJ
          </h1>
          <p className="text-xl text-warm-gray max-w-2xl mx-auto leading-relaxed">
            Conseils, inspirations et tendances pour réussir votre mariage ou soirée d'entreprise.
          </p>
        </div>
      </section>

      {/* Articles */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {loading && (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div className="text-center py-20">
              <BookOpen className="h-16 w-16 text-warm-gray/40 mx-auto mb-4" />
              <p className="text-xl text-warm-gray font-medium mb-2">
                Les articles arrivent bientôt...
              </p>
              <p className="text-warm-gray text-sm">
                Revenez dans quelques jours pour découvrir nos premiers conseils.
              </p>
            </div>
          )}

          {!loading && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post) => (
                <article key={post.id} className="group bg-white rounded-2xl border border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col">
                  {post.cover_image_url && (
                    <div className="overflow-hidden h-48">
                      <img
                        src={post.cover_image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-900 bg-blue-50 px-2 py-1 rounded-full">
                            <Tag className="h-3 w-3" />{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Date */}
                    <div className="flex items-center gap-1.5 text-xs text-warm-gray mb-3">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <h2 className="text-xl font-bold text-charcoal group-hover:text-blue-900 transition-colors mb-3 leading-snug">
                      {post.title}
                    </h2>
                    <p className="text-warm-gray text-sm leading-relaxed flex-1 mb-4">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-blue-900 font-semibold hover:text-gold transition-colors text-sm mt-auto"
                    >
                      Lire l'article
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Un projet mariage ou soirée d'entreprise ?
          </h2>
          <p className="text-white/80 mb-8">Discutons-en, devis gratuit sans engagement.</p>
          <Button size="lg" asChild className="bg-white text-blue-900 hover:bg-ivory font-bold border-0">
            <Link to="/contact">Demander un devis <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

    </div>
  );
};
